import { useQuery } from "@apollo/client";
import { Button } from "src/components/ui/button";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "lucide-react";
import { GET_COMMUNITY_ANNOUNCEMENT } from "src/features/communities/graphql";
import { formatAnnouncementType } from "src/utlis/helpers";
import { RequestFailed } from "src/components/RequestFailed";

export default function AnnouncementDetailsPage() {
	const { id } = useParams<{ id: string }>();

	const { data, loading, error } = useQuery(GET_COMMUNITY_ANNOUNCEMENT, {
		variables: { announcementId: id! },
		skip: !id,
	});

	if (loading) {
		return <div>Loading...</div>;
	}

	if (error) return <RequestFailed text={error.message} />;

	const announcement = data?.getCommunityAnnouncementForPro;

	if (!announcement) {
		return <div>Announcement not found</div>;
	}

	const hasHTML = /<\/?[a-z][\s\S]*>/i.test(announcement.content);

	return (
		<div className="flex flex-col gap-6">
			<div className="flex items-center justify-between gap-[28px] mb-4 pb-4 border-b border-[input]">
				<div className="flex items-center gap-[28px]">
					<Button
						asChild
						variant="outline"
						className="h-8 w-8 p-0 rounded-full border-[#B8B8B8] border-[1.5px]"
					>
						<Link to="/communities">
							<ArrowLeftIcon className="w-4 h-4" />
						</Link>
					</Button>
					<h1 className="text-lg lg:text-2xl font-semibold">Announcement</h1>
				</div>
			</div>

			<div className="border border-gray-300 bg-white">
				<div className="grid grid-cols-1 lg:grid-cols-2 min-h-[412px]">
					{/* Left Side - Metadata */}
					<div className="p-6 space-y-5">
						{/* Announcement Type */}
						<div className="space-y-3">
							<div className="text-gray-500 text-sm font-normal">Type</div>
							<div className="text-gray-900 text-sm font-medium">
								{formatAnnouncementType(announcement.type)}
							</div>
						</div>

						{/* Divider */}
						<div className="border-t border-gray-200"></div>

						{/* Announcement Title */}
						<div className="space-y-3">
							<div className="text-gray-500 text-sm font-normal">Title</div>
							<div className="text-gray-900 text-sm font-medium">
								{announcement.title}
							</div>
						</div>

						{/* Divider */}
						<div className="border-t border-gray-200"></div>
					</div>

					{/* Right Side - Content */}
					<div className="p-6 border-t lg:border-t-0 border-l-0 lg:border-l border-gray-200">
						<div className="space-y-3">
							<div className="text-gray-500 text-sm font-normal">Content</div>
							{hasHTML ? (
								<div
									className="prose prose-sm max-w-none lg:min-h-[200px] max-h-[300px] overflow-y-auto border-t py-2 border-gray-200"
									dangerouslySetInnerHTML={{
										__html: announcement.content,
									}}
								/>
							) : (
								<div className="prose prose-sm max-w-none lg:min-h-[200px] max-h-[300px] overflow-y-auto border-t py-2 border-gray-200">
									{announcement.content}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
